/**

 * AGC: Interactive PvE Battle Component - On-chain battle flow (PvEBattleManagerV4)

 * AGC: Mirrors hybrid layout while streaming canonical turn data from contract storage

 */



import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'

import { ethers } from 'ethers'

import { usePlayerProgression } from '../hooks/usePlayerProgression'

import { PlayerProgressionPanel } from './PlayerProgressionPanel'

import { RARITY_INFO, type Rarity } from '../game/PvEGameEngine'

import { playSoundSafe, unlockAudioPlayback } from '../utils/audio'
import { BattleTurnModal, BattleTurnSnapshot } from './modals/BattleTurnModal'
import { formatRarityLabel, getRarityBadgeClasses } from '../utils/rarityBadges'



interface InteractivePvEBattleProps {

  battleId: string

  playerAddress: string

  botAddress: string

  botLevel: number

  selectedBuffs: number[] // AGC: NFT buff IDs the player escrowed for this battle
  selectedPotion?: number | null // AGC: Potion token ID the player escrowed for this battle

  onBattleEnd: (won: boolean, turns: number, isDraw?: boolean, resolutionTxHash?: string | null) => void

  onBackToMenu: () => void

}



type Choice = 'rock' | 'paper' | 'scissors'

type Element = 'water' | 'fire' | 'ice' | 'wind'



type ContractTurn = {

  playerHand: number

  playerElement: number

  botHand: number

  botElement: number

  playerDamage: bigint | number

  botDamage: bigint | number

  playerBuffs?: Array<bigint | number>

  botBuffs?: Array<bigint | number>

}



interface TurnResult {

  turnNumber: number

  yourDamage: number

  opponentDamage: number

  yourEnergy: number

  opponentEnergy: number

  yourChoice: Choice

  opponentChoice: Choice

  yourElement: Element | null

  opponentElement: Element | null

  yourBuffs: number[]

  opponentBuffs: number[]

  yourActivatedBuffs: BuffName[]

  opponentActivatedBuffs: BuffName[]

  yourFrostUsed: boolean

  opponentFrostUsed: boolean

  yourRarity: Rarity

  opponentRarity: Rarity

  yourBreakdown?: DamageBreakdown

  opponentBreakdown?: DamageBreakdown

}

const PVE_BUFF_LABELS: Record<number, string> = {
  0: 'SHIELD',
  1: 'PIERCE',
  2: 'HASTE',
  3: 'FROST',
}

const mapPvETurnToSnapshot = (turn: TurnResult): BattleTurnSnapshot => {
  const toChips = (buffIds: number[], activated: BuffName[] = [], frostUsed: boolean) =>
    buffIds.map((id) => {
      const label = PVE_BUFF_LABELS[id] ?? `BUFF ${id}`
      const isActive = activated?.some((buff) => buff.toUpperCase() === label)
      let status: 'active' | 'ready' | 'used' = isActive ? 'active' : 'ready'
      if (label === 'FROST' && frostUsed) {
        status = 'used'
      }
      return { label, status }
    })

  return {
    turnNumber: turn.turnNumber,
    yourChoice: turn.yourChoice,
    opponentChoice: turn.opponentChoice,
    yourElement: turn.yourElement ?? undefined,
    opponentElement: turn.opponentElement ?? undefined,
    yourDamage: turn.yourDamage,
    opponentDamage: turn.opponentDamage,
    yourEnergy: turn.yourEnergy,
    opponentEnergy: turn.opponentEnergy,
    yourBuffs: toChips(turn.yourBuffs ?? [], turn.yourActivatedBuffs ?? [], turn.yourFrostUsed),
    opponentBuffs: toChips(turn.opponentBuffs ?? [], turn.opponentActivatedBuffs ?? [], turn.opponentFrostUsed),
    yourBreakdown: turn.yourBreakdown,
    opponentBreakdown: turn.opponentBreakdown,
  }
}



const HAND_MAP: ReadonlyArray<Choice> = ['rock', 'paper', 'scissors'] as const

const ELEMENT_MAP = ['none', 'fire', 'water', 'ice', 'wind'] as const

const BUFF_ID_TO_NAME: Record<number, 'SHIELD' | 'PIERCE' | 'HASTE' | 'FROST'> = {

  0: 'SHIELD',

  1: 'PIERCE',

  2: 'HASTE',

  3: 'FROST'

}



interface DamageBreakdown {

  baseName: string

  baseDamage: number

  levelBonus: number

  elementBonus: number

  elementName: 'ADVANTAGE' | 'NEUTRAL' | 'DISADVANTAGE'

  rarityBonus: number

  rarityName: string

  hasteBonus: number

  rawDamage: number

  defense: number

  afterDefense: number

  pierceReduction?: number

  shieldReduction?: number

  frostReduction?: number

  baseWithoutLevel?: number

  handOutcome?: 'WIN' | 'DRAW' | 'LOSE'

  handMultiplier?: number

  damageAfterHand?: number

  damageAfterElement?: number

  damageAfterCrit?: number

  elementMultiplier?: number

  critApplied?: boolean

  hasteApplied?: boolean

  pierceApplied?: boolean

  shieldApplied?: boolean

  frostApplied?: boolean

  attackerLevel?: number

  defenderLevel?: number

  finalDamage?: number

}



type BuffName = 'SHIELD' | 'PIERCE' | 'HASTE' | 'FROST'



const OUTCOME_BASE_DAMAGE: Record<'WIN' | 'DRAW' | 'LOSE', number> = {

  WIN: 30,

  DRAW: 15,

  LOSE: 10

}



const ELEMENT_BONUS_MAP: Record<'ADVANTAGE' | 'NEUTRAL' | 'DISADVANTAGE', number> = {

  ADVANTAGE: 10,

  NEUTRAL: 0,

  DISADVANTAGE: -5

}



const RARITY_BONUS_MAP: Record<Rarity, number> = {

  DIAMOND: 50,

  GOLD: 25,

  SILVER: 10,

  BRONZE: 5,

  NONE: 0

}



const RARITY_PRIORITY: Rarity[] = ['DIAMOND', 'GOLD', 'SILVER', 'BRONZE', 'NONE']



const ELEMENT_ADVANTAGE: Record<Element, Element | null> = {

  water: 'fire',

  fire: 'ice',

  ice: 'wind',

  wind: 'water'

}



const toUpperChoice = (choice: Choice): 'ROCK' | 'PAPER' | 'SCISSORS' => choice.toUpperCase() as 'ROCK' | 'PAPER' | 'SCISSORS'



const determineRpsOutcome = (you: Choice, opponent: Choice): 'WIN' | 'LOSE' | 'DRAW' => {

  if (you === opponent) return 'DRAW'

  if (

    (you === 'rock' && opponent === 'scissors') ||

    (you === 'paper' && opponent === 'rock') ||

    (you === 'scissors' && opponent === 'paper')

  ) {

    return 'WIN'

  }

  return 'LOSE'

}



const elementRelationship = (attacker: Element | null, defender: Element | null): 'ADVANTAGE' | 'NEUTRAL' | 'DISADVANTAGE' => {

  if (!attacker || !defender) {

    return 'NEUTRAL'

  }

  if (ELEMENT_ADVANTAGE[attacker] === defender) {

    return 'ADVANTAGE'

  }

  if (ELEMENT_ADVANTAGE[defender] === attacker) {

    return 'DISADVANTAGE'

  }

  return 'NEUTRAL'

}



const extractTransactionHash = (event: unknown): string | null => {

  if (!event || typeof event !== 'object') {

    return null

  }



  const eventObject = event as {

    transactionHash?: string

    log?: { transactionHash?: string }

    receipt?: { transactionHash?: string }

  }



  return (

    eventObject.transactionHash ||

    eventObject.log?.transactionHash ||

    eventObject.receipt?.transactionHash ||

    null

  )

}



const formatBonusLabel = (value?: number): string => {

  if (value === undefined || value === null || Number.isNaN(value)) {

    return '(0)'

  }



  const rounded = Math.round(value)



  if (rounded > 0) {

    return `(+${rounded})`

  }



  if (rounded < 0) {

    return `(${rounded})`

  }



  return '(0)'

}



const mapBuffIdsToNames = (buffIds: number[]): BuffName[] =>

  buffIds

    .map((id) => BUFF_ID_TO_NAME[id])

    .filter((name): name is BuffName => Boolean(name))



interface SimulationContext {

  attackerLevel: number

  defenderLevel: number

  attackerChoice: Choice

  defenderChoice: Choice

  attackerElement: Element | null

  defenderElement: Element | null

  attackerActivatedBuffIds: number[]

  defenderHasShield: boolean

  defenderHasFrost: boolean

  actualDamage: number

}



const mapHandValue = (value: number): Choice => HAND_MAP[value] ?? 'rock'



const mapElementValue = (value: number): Element | null => {

  const mapped = ELEMENT_MAP[value]

  if (!mapped || mapped === 'none') {

    return null

  }

  return mapped as Element

}



const simulateContractDamage = ({

  attackerLevel,

  defenderLevel,

  attackerChoice,

  defenderChoice,

  attackerElement,

  defenderElement,

  attackerActivatedBuffIds,

  defenderHasShield,

  defenderHasFrost,

  actualDamage

}: SimulationContext): { breakdown: DamageBreakdown; frostApplied: boolean; shieldApplied: boolean } => {

  const attackerBuffNames = mapBuffIdsToNames(attackerActivatedBuffIds)



  const handOutcome = determineRpsOutcome(attackerChoice, defenderChoice)

  const baseOutcomeDamage = OUTCOME_BASE_DAMAGE[handOutcome]

  const levelBonus = attackerLevel

  const elementRelation = elementRelationship(attackerElement, defenderElement)

  const elementBonus = ELEMENT_BONUS_MAP[elementRelation]



  const damageAfterLevel = baseOutcomeDamage + levelBonus

  const damageAfterElement = damageAfterLevel + elementBonus



  const hasteActivated = attackerBuffNames.includes('HASTE')

  const pierceActivated = attackerBuffNames.includes('PIERCE')



  const defenseMultiplier = Math.max(0.5, 1 - defenderLevel / 200)



  const rarityCandidates = RARITY_PRIORITY.map((rarityName) => ({

    rarityName,

    rarityBonus: RARITY_BONUS_MAP[rarityName]

  }))



  let bestMatch: {

    breakdown: DamageBreakdown

    frostApplied: boolean

    shieldApplied: boolean

    diff: number

  } | null = null



  for (const { rarityName, rarityBonus } of rarityCandidates) {

    const hasteBonus = hasteActivated ? 5 : 0



    const damageAfterRarity = damageAfterElement + rarityBonus

    const rawDamage = damageAfterRarity + hasteBonus

    const damageAfterPierce = pierceActivated ? rawDamage * 1.25 : rawDamage

    const pierceBonus = pierceActivated ? damageAfterPierce - rawDamage : 0



    const damageAfterDefense = damageAfterPierce * defenseMultiplier



    let postDefenseDamage = damageAfterDefense

    let frostReduction = 0

    if (defenderHasFrost && postDefenseDamage > 0) {

      frostReduction = postDefenseDamage

      postDefenseDamage = 0

    }



    let shieldReduction = 0

    if (defenderHasShield && postDefenseDamage > 0) {

      shieldReduction = postDefenseDamage * 0.25

      postDefenseDamage = postDefenseDamage * 0.75

    }



    const finalDamage = Math.round(postDefenseDamage)

    const diff = Math.abs(finalDamage - actualDamage)



    const breakdown: DamageBreakdown = {

      baseName: toUpperChoice(attackerChoice),

      baseDamage: damageAfterLevel,

      baseWithoutLevel: baseOutcomeDamage,

      levelBonus,

      elementBonus,

      elementName: elementRelation,

      rarityBonus,

      rarityName,

      hasteBonus,

      rawDamage,

      defense: defenseMultiplier,

      afterDefense: damageAfterDefense,

      pierceReduction: pierceBonus > 0 ? pierceBonus : undefined,

      shieldReduction: shieldReduction > 0 ? shieldReduction : undefined,

      frostReduction: frostReduction > 0 ? frostReduction : undefined,

      finalDamage,

      handOutcome,

      handMultiplier: 1,

      damageAfterHand: damageAfterLevel,

      damageAfterElement,

      damageAfterCrit: damageAfterRarity,

      elementMultiplier: 1,

      critApplied: rarityBonus > 0,

      hasteApplied: hasteActivated,

      pierceApplied: pierceActivated,

      shieldApplied: shieldReduction > 0,

      frostApplied: frostReduction > 0,

      attackerLevel,

      defenderLevel

    }



    const isBetterMatch =

      !bestMatch ||

      diff < bestMatch.diff ||

      (diff === bestMatch.diff && rarityBonus > (bestMatch.breakdown.rarityBonus ?? 0))



    if (isBetterMatch) {

      bestMatch = {

        breakdown,

        frostApplied: frostReduction > 0,

        shieldApplied: shieldReduction > 0,

        diff

      }

    }



    if (diff === 0) {

      break

    }

  }



  if (!bestMatch) {

    return {

      breakdown: {

        baseName: toUpperChoice(attackerChoice),

        baseDamage: damageAfterLevel,

        baseWithoutLevel: baseOutcomeDamage,

        levelBonus,

        elementBonus,

        elementName: elementRelation,

        rarityBonus: 0,

        rarityName: 'NONE',

        hasteBonus: hasteActivated ? 5 : 0,

        rawDamage: actualDamage,

        defense: defenseMultiplier,

        afterDefense: actualDamage,

        finalDamage: actualDamage,

        handOutcome,

        handMultiplier: 1,

        damageAfterHand: damageAfterLevel,

        damageAfterElement,

        damageAfterCrit: damageAfterElement,

        elementMultiplier: 1,

        critApplied: false,

        hasteApplied: hasteActivated,

        pierceApplied: pierceActivated,

        shieldApplied: false,

        frostApplied: false,

        attackerLevel,

        defenderLevel

      },

      frostApplied: false,

      shieldApplied: false

    }

  }



  const adjustedBreakdown = {

    ...bestMatch.breakdown,

    finalDamage: actualDamage

  }



  return {

    breakdown: adjustedBreakdown,

    frostApplied: bestMatch.frostApplied,

    shieldApplied: bestMatch.shieldApplied

  }

}



const mapContractTurnsToResults = (

  turns: ContractTurn[],

  playerBuffIds: number[],

  playerLevel: number,

  botLevel: number

): TurnResult[] => {

  let playerEnergy = 100

  let botEnergy = 100

  let playerFrostUsed = false

  let botFrostUsed = false



  return turns.map((turn, index) => {

    const playerBuffActivations = (turn.playerBuffs ?? [])

      .map((value) => Number(value))

      .filter((id) => Number.isFinite(id))



    const botBuffActivations = (turn.botBuffs ?? [])

      .map((value) => Number(value))

      .filter((id) => Number.isFinite(id))



    if (playerBuffActivations.includes(3)) {

      playerFrostUsed = true

    }



    if (botBuffActivations.includes(3)) {

      botFrostUsed = true

    }



    const yourDamage = Number(turn.playerDamage ?? 0)

    const opponentDamage = Number(turn.botDamage ?? 0)



    const yourChoice = mapHandValue(Number(turn.playerHand))

    const opponentChoice = mapHandValue(Number(turn.botHand))

    const yourElement = mapElementValue(Number(turn.playerElement))

    const opponentElement = mapElementValue(Number(turn.botElement))



    const { breakdown: yourBreakdown } = simulateContractDamage({

      attackerLevel: playerLevel,

      defenderLevel: botLevel,

      attackerChoice: yourChoice,

      defenderChoice: opponentChoice,

      attackerElement: yourElement,

      defenderElement: opponentElement,

      attackerActivatedBuffIds: playerBuffActivations,

      defenderHasShield: botBuffActivations.includes(0),

      defenderHasFrost: botBuffActivations.includes(3),

      actualDamage: yourDamage

    })



    const botSimulation = simulateContractDamage({

      attackerLevel: botLevel,

      defenderLevel: playerLevel,

      attackerChoice: opponentChoice,

      defenderChoice: yourChoice,

      attackerElement: opponentElement,

      defenderElement: yourElement,

      attackerActivatedBuffIds: botBuffActivations,

      defenderHasShield: playerBuffIds.includes(0),

      defenderHasFrost: playerBuffIds.includes(3) && !playerFrostUsed,

      actualDamage: opponentDamage

    })



    const botBreakdown = botSimulation.breakdown



    if (yourBreakdown.frostApplied) {

      botFrostUsed = true

    }



    if (botSimulation.frostApplied) {

      playerFrostUsed = true

    }



    botEnergy = Math.max(0, botEnergy - yourDamage)

    playerEnergy = Math.max(0, playerEnergy - opponentDamage)



    botFrostUsed = botFrostUsed || botSimulation.frostApplied



    const offensivePlayerBuffs = mapBuffIdsToNames(playerBuffActivations)

    const defensivePlayerBuffs: BuffName[] = []

    if (botSimulation.frostApplied) {

      defensivePlayerBuffs.push('FROST')

    }

    if (botSimulation.shieldApplied) {

      defensivePlayerBuffs.push('SHIELD')

    }

    const yourActivatedBuffs = Array.from(new Set([...offensivePlayerBuffs, ...defensivePlayerBuffs]))



    const offensiveBotBuffs = mapBuffIdsToNames(botBuffActivations)

    const defensiveBotBuffs: BuffName[] = []

    if (yourBreakdown.frostApplied) {

      defensiveBotBuffs.push('FROST')

    }

    if (yourBreakdown.shieldApplied) {

      defensiveBotBuffs.push('SHIELD')

    }

    const opponentActivatedBuffs = Array.from(new Set([...offensiveBotBuffs, ...defensiveBotBuffs]))



    return {

      turnNumber: index + 1,

      yourDamage,

      opponentDamage,

      yourEnergy: playerEnergy,

      opponentEnergy: botEnergy,

      yourChoice,

      opponentChoice,

      yourElement,

      opponentElement,

      yourBuffs: playerBuffIds,

      opponentBuffs: botBuffActivations,

      yourActivatedBuffs,

      opponentActivatedBuffs,

      yourFrostUsed: playerFrostUsed,

      opponentFrostUsed: botFrostUsed,

      yourRarity: (yourBreakdown.rarityName ?? 'NONE') as Rarity,

      opponentRarity: (botBreakdown.rarityName ?? 'NONE') as Rarity,

      yourBreakdown,

      opponentBreakdown: botBreakdown

    }

  })

}



const DEFAULT_CHOICE: Choice = 'rock'
const DEFAULT_ELEMENT: Element = 'water'

const renderRarityBadge = (rarityName?: string | null) => {
  const classes = getRarityBadgeClasses(rarityName)
  if (!classes) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      <span className={`px-2 py-1 rounded text-xs font-semibold ${classes}`}>
        {formatRarityLabel(rarityName)}
      </span>
    </div>
  )
}

export const InteractivePvEBattle: React.FC<InteractivePvEBattleProps> = ({

  battleId,

  playerAddress,

  botAddress: _botAddress,

  botLevel,

  selectedBuffs,

  onBattleEnd,

  onBackToMenu

}) => {

  // AGC: Bot address reserved for future UI/telemetry use

  void _botAddress

  // AGC: Load player stats for the progression panel + energy layout

  const { playerStats } = usePlayerProgression(playerAddress)



  const [currentTurn, setCurrentTurn] = useState(1)

  const [yourEnergy, setYourEnergy] = useState(100)

  const [opponentEnergy, setOpponentEnergy] = useState(100)

  const [turnTimeLeft, setTurnTimeLeft] = useState(60)

  

  const [selectedChoice, setSelectedChoice] = useState<Choice | null>(DEFAULT_CHOICE)

  const [element, setElement] = useState<Element | null>(DEFAULT_ELEMENT)

  const [turnSubmitted, setTurnSubmitted] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)

  

  const [battleLog, setBattleLog] = useState<TurnResult[]>([])
  const [battleEnded, setBattleEnded] = useState(false)
  const [winner, setWinner] = useState<string | null>(null)
  const [resolutionHash, setResolutionHash] = useState<string | null>(null)
  const [turnSnapshot, setTurnSnapshot] = useState<BattleTurnSnapshot | null>(null)



  const previousLogLengthRef = useRef(0)



  useEffect(() => {

    const previousLength = previousLogLengthRef.current

    if (battleLog.length > previousLength) {

      playSoundSafe('/sounds/punch.mp3', 0.5)

    }

    previousLogLengthRef.current = battleLog.length

  }, [battleLog.length])



  const normalizedBuffIds = useMemo(() => selectedBuffs.map((value) => Number(value)), [selectedBuffs])



  const processedTurnCount = useRef(0)

  const isComponentMounted = useRef(true)



  useEffect(() => {

    return () => {

      isComponentMounted.current = false

    }

  }, [])



  // AGC: Reset local state when switching battles

  useEffect(() => {

    processedTurnCount.current = 0

    setBattleLog([])

    setBattleEnded(false)

    setWinner(null)

    setResolutionHash(null)

    setYourEnergy(100)

    setOpponentEnergy(100)

    setCurrentTurn(1)

    setTurnTimeLeft(60)

    setSelectedChoice(DEFAULT_CHOICE)

    setElement(DEFAULT_ELEMENT)

    setTurnSubmitted(false)

    setIsSubmitting(false)

  }, [battleId])



  const PVE_MANAGER_ADDRESS = import.meta.env.VITE_PVE_MANAGER_V4_ADDRESS

  const PVE_MANAGER_ABI = [

    'function playTurn(bytes32 battleId, uint8 playerHand, uint8 playerElement) external',

    'function getBattle(bytes32 battleId) external view returns (tuple(bytes32 battleId, address player, address bot, uint256 stake, bool playerPaid, bool botPaid, bool resolved, address winner, uint8 playerHP, uint8 botHP, uint8 playerLevel, uint8 botLevel, uint8 currentTurn, uint256 createdAt, uint256 resolvedAt, bool refunded))',

    'function getBattleTurns(bytes32 battleId) external view returns (tuple(uint8 playerHand, uint8 playerElement, uint8 botHand, uint8 botElement, uint8 playerDamage, uint8 botDamage, uint256[] playerBuffs, uint256[] botBuffs, uint256 timestamp)[])',

    'event TurnPlayed(bytes32 indexed battleId, uint8 turnNumber, uint8 playerHand, uint8 playerElement, uint8 botHand, uint8 botElement, uint8 playerDamage, uint8 botDamage, uint8 playerHP, uint8 botHP)',

    'event BattleResolved(bytes32 indexed battleId, address indexed winner, address indexed loser)',

    'event BattleDraw(bytes32 indexed battleId, address player, address bot)'

  ]



  const refreshTurnsFromChain = useCallback(

    async (contractInstance: ethers.Contract): Promise<TurnResult[]> => {

      try {

        if (typeof contractInstance.getBattleTurns !== 'function') {

          console.warn('[WARN]  PvE manager ABI missing getBattleTurns - skipping turn hydration')

          return []

        }



        const turnsFromChain: ContractTurn[] = await contractInstance.getBattleTurns(battleId)

        const mappedResults = mapContractTurnsToResults(
          turnsFromChain,
          normalizedBuffIds,
          playerStats?.level || 1,
          botLevel
        )

        const previousCount = processedTurnCount.current
        processedTurnCount.current = mappedResults.length
        setBattleLog(mappedResults)

        if (mappedResults.length > 0) {
          const lastTurn = mappedResults[mappedResults.length - 1]
          setYourEnergy(lastTurn.yourEnergy)
          setOpponentEnergy(lastTurn.opponentEnergy)
          setCurrentTurn(mappedResults.length + 1)

          if (mappedResults.length > previousCount) {
            setTurnSnapshot(mapPvETurnToSnapshot(lastTurn))
          }
        } else {
          setYourEnergy(100)
          setOpponentEnergy(100)
          setCurrentTurn(1)
        }

        return mappedResults

      } catch (error) {

        console.error('[ERROR] Failed to hydrate on-chain turns:', (error as Error).message)

        return []

      }

    },

    [battleId, normalizedBuffIds, playerStats?.level, botLevel]

  )



  // AGC: Countdown per turn (auto-submit random choice on timeout)

  useEffect(() => {

    if (turnSubmitted || battleEnded) return

    

    const timer = setInterval(() => {

      setTurnTimeLeft((prev) => {

        if (prev <= 1) {

          if (!turnSubmitted && !isSubmitting) {

            handleAutoSubmit()

          }

          return 60

        }

        return prev - 1

      })

    }, 1000)

    

    return () => clearInterval(timer)

  }, [turnSubmitted, battleEnded, isSubmitting])



  // AGC: Subscribe to PvE manager events for real-time updates

  useEffect(() => {

    if (!PVE_MANAGER_ADDRESS) {

      console.error('[ERROR] PVE manager V4 address missing in env (VITE_PVE_MANAGER_V4_ADDRESS)')

          return

        }



    let provider: ethers.JsonRpcProvider | null = null

    let contractRef: ethers.Contract | null = null

    let active = true



    const initialize = async () => {

      try {

        provider = new ethers.JsonRpcProvider('https://sepolia.base.org')

        contractRef = new ethers.Contract(PVE_MANAGER_ADDRESS, PVE_MANAGER_ABI, provider)



        console.log(' On-chain PvE listener initialised for battle:', battleId)



        await refreshTurnsFromChain(contractRef)



        const handleTurnPlayed = async (

          eventBattleId: string,

          turnNumber: bigint,

          _playerHand: number,

          _playerElement: number,

          _botHand: number,

          _botElement: number,

          _playerDamage: bigint,

          _botDamage: bigint,

          _playerHP: bigint,

          _botHP: bigint

        ) => {

          if (!active || eventBattleId !== battleId || !contractRef) {

            return

          }



          const turnIndex = Number(turnNumber)

          if (turnIndex <= processedTurnCount.current) {

            return

          }



          const mapped = await refreshTurnsFromChain(contractRef)

          if (!isComponentMounted.current) {

            return

          }



          setTurnSubmitted(false)

          setIsSubmitting(false)

          setTurnTimeLeft(60)

          setSelectedChoice(DEFAULT_CHOICE)

          setElement(DEFAULT_ELEMENT)



          if (mapped.length > 0) {

            playSoundSafe('/sounds/punch.mp3', 0.5)

          }

        }



        const finalizeBattle = async (

          eventBattleId: string,

          winnerAddress: string | null,

          isDraw: boolean,

          resolutionTxHash?: string | null

        ) => {

          if (!active || eventBattleId !== battleId || !contractRef) {

            return

          }



          const mapped = await refreshTurnsFromChain(contractRef)

          if (!isComponentMounted.current) {

            return

          }



        setBattleEnded(true)
        setTurnSubmitted(false)
        setIsSubmitting(false)
        setTurnTimeLeft(0)
        setWinner(winnerAddress ? winnerAddress.toLowerCase() : null)
        setResolutionHash(resolutionTxHash ?? null)
        setTurnSnapshot(null)



          const totalTurns = mapped.length > 0 ? mapped[mapped.length - 1].turnNumber : processedTurnCount.current || 1

          const playerWon = Boolean(winnerAddress && winnerAddress.toLowerCase() === playerAddress.toLowerCase())



        setTimeout(() => {

            if (!isComponentMounted.current) {

              return

            }

            onBattleEnd(isDraw ? false : playerWon, totalTurns, isDraw, resolutionTxHash ?? null)

          }, 1500)

        }



        const handleBattleResolved = async (

          eventBattleId: string,

          winnerAddress: string,

          _loserAddress: string,

          event: unknown

        ) => {

          const txHash = extractTransactionHash(event)

          await finalizeBattle(eventBattleId, winnerAddress, false, txHash)

        }



        const handleBattleDraw = async (

          eventBattleId: string,

          _playerAddress: string,

          _botAddress: string,

          event: unknown

        ) => {

          const txHash = extractTransactionHash(event)

          await finalizeBattle(eventBattleId, null, true, txHash)

        }



        contractRef.on('TurnPlayed', handleTurnPlayed)

        contractRef.on('BattleResolved', handleBattleResolved)

        contractRef.on('BattleDraw', handleBattleDraw)

      } catch (error) {

        console.error('[ERROR] Failed to initialise PvE on-chain listener:', (error as Error).message)

      }

    }



    initialize()



    return () => {

      active = false

      if (contractRef) {

        contractRef.removeAllListeners('TurnPlayed')

        contractRef.removeAllListeners('BattleResolved')

        contractRef.removeAllListeners('BattleDraw')

      }

    }

  }, [PVE_MANAGER_ADDRESS, battleId, playerAddress, refreshTurnsFromChain])



  const handleAutoSubmit = () => {

    const choices: Choice[] = ['rock', 'paper', 'scissors']

    const elements: Element[] = ['water', 'fire', 'ice', 'wind']

    const randomChoice = choices[Math.floor(Math.random() * choices.length)]

    const randomElement = elements[Math.floor(Math.random() * elements.length)]

    

    setSelectedChoice(randomChoice)

    setElement(randomElement)

    handleSubmitTurn(randomChoice, randomElement)

  }



  const handleSubmitTurn = async (choice: Choice = selectedChoice!, elem: Element | null = element) => {

    if (!choice || isSubmitting) return



    unlockAudioPlayback()

    setIsSubmitting(true)

    setTurnSubmitted(true)



    try {

      console.log(` Submitting turn ${currentTurn}:`, { choice, element: elem, battleId })



      if (!window.ethereum) {

        throw new Error('No wallet detected. Please install MetaMask or Coinbase Wallet.')

      }



      const browserProvider = new ethers.BrowserProvider(window.ethereum)



      let chainIdHex: string | null = null

      try {

        chainIdHex = await browserProvider.send('eth_chainId', [])

      } catch (chainError) {

        console.warn(

          '[WARN] AGC: Unable to read chain id from embedded wallet – likely preview environment blocking JSON-RPC.',

          chainError

        )

        setIsSubmitting(false)

        setTurnSubmitted(false)

        alert(

          'The Base preview wallet cannot sign Sepolia transactions. Please test this turn on the desktop build or a real Farcaster session.'

        )

        return

      }



      if (!chainIdHex || parseInt(chainIdHex, 16) !== 84532) {

        console.warn(

          `[WARN] AGC: Player turn requires Base Sepolia (84532) but detected chain ${chainIdHex}. Skipping on-chain submission.`

        )

        setIsSubmitting(false)

        setTurnSubmitted(false)

        alert(

          'This simulator is wired to Base Sepolia. Switch to Sepolia or use the desktop build to play turns.'

        )

        return

      }



      const signer = await browserProvider.getSigner()

      const writeContract = new ethers.Contract(PVE_MANAGER_ADDRESS, PVE_MANAGER_ABI, signer)

      const readOnlyProvider = signer.provider ?? browserProvider

      if (!readOnlyProvider) {

        throw new Error('Unable to access RPC provider')

      }

      const contract = writeContract



      try {

        console.log(' DEBUG: Checking battle state before submission...')

        const battleABI = ['function battles(bytes32) view returns (bytes32 battleId, address player, address bot, uint256 stake, bool playerPaid, bool botPaid, bool resolved, address winner, uint8 playerHP, uint8 botHP, uint8 playerLevel, uint8 botLevel, uint8 currentTurn, uint256 createdAt, uint256 resolvedAt, bool refunded)']

        const checkContract = new ethers.Contract(PVE_MANAGER_ADDRESS, battleABI, readOnlyProvider)

        const battle = await checkContract.battles(battleId)

        

        if (!battle.playerPaid || !battle.botPaid) {

          throw new Error('Both players must pay before starting battle')

        }

        if (battle.resolved) {

          throw new Error('Battle has already ended')

        }

        if (battle.refunded) {

          throw new Error('Battle was refunded')

        }

        if (battle.playerHP === 0n || battle.botHP === 0n) {

          throw new Error('Battle has ended (HP = 0)')

        }

      } catch (debugError: any) {

        if (

          debugError.message?.includes('chain-proxy.wallet.coinbase.com') ||

            debugError.message?.includes('CORS') ||

            debugError.message?.includes('502') ||

          debugError.message?.includes('Bad Gateway')

        ) {

          console.log('[INFO] Coinbase Wallet proxy error (ignoring)')

        } else {

          console.error('[WARN] Debug check failed:', debugError.message)

          throw debugError

        }

      }



      const handEnum = choice === 'rock' ? 0 : choice === 'paper' ? 1 : 2

      const elementEnum = !elem ? 0 : elem === 'fire' ? 1 : elem === 'water' ? 2 : elem === 'ice' ? 3 : 4



      try {

        const gasEstimate = await contract.playTurn.estimateGas(battleId, handEnum, elementEnum)

        console.log(`• Gas estimate: ${gasEstimate.toString()}`)

      } catch (gasError: any) {

        console.error('[ERROR] Gas estimation failed:', gasError)

        throw new Error(`Transaction will fail: ${gasError.reason || gasError.message}`)

      }



      try {

        await contract.playTurn.staticCall(battleId, handEnum, elementEnum)

      } catch (staticError: any) {

        const staticReason = staticError.reason || staticError.shortMessage || staticError.message || 'Unknown revert reason'

        throw new Error(`Transaction will fail: ${staticReason}`)

      }



      const tx = await contract.playTurn(battleId, handEnum, elementEnum)

      console.log(`[OK] Transaction sent: ${tx.hash}`)

      console.log(` View on BaseScan: https://sepolia.basescan.org/tx/${tx.hash}`)



      const receipt = await tx.wait()

      if (receipt.status === 0) {

        throw new Error('Transaction reverted (check console for details)')

      }



      console.log(`[OK] Transaction confirmed in block ${receipt.blockNumber}!`)



      if (readOnlyProvider) {

        const readContract = new ethers.Contract(PVE_MANAGER_ADDRESS, PVE_MANAGER_ABI, readOnlyProvider)

        await refreshTurnsFromChain(readContract)

      }



      setIsSubmitting(false)

      setTurnSubmitted(false)

      setTurnTimeLeft(60)

      setSelectedChoice(DEFAULT_CHOICE)

      setElement(DEFAULT_ELEMENT)

    } catch (error: any) {

      console.error('[ERROR] Error submitting turn:', error)

      let errorMessage = 'Failed to submit turn'

      if (error.message?.includes('user rejected')) {

        errorMessage = 'Transaction was rejected by user'

      } else if (error.reason) {

        errorMessage = `Contract error: ${error.reason}`

      } else if (error.message) {

        errorMessage = error.message

      }

      alert(errorMessage)

      setIsSubmitting(false)

      setTurnSubmitted(false)

    }

  }



  const getChoiceImage = (choice: Choice) => {

    const images = {

      rock: '/rock.png',

      paper: '/paper.png',

      scissors: '/scissor.png'

    }

    return images[choice]

  }



  const getElementEmoji = (elem: Element | null) => {
    if (!elem) return '—'
    const emojis: Record<Element, string> = {
      water: '💧',
      fire: '🔥',
      ice: '❄️',
      wind: '💨'
    }
    return emojis[elem]

  }



  useEffect(() => {

    processedTurnCount.current = 0

    setBattleLog([])

    setTurnSnapshot(null)

  }, [battleId])



  return (

    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">

      <div className="max-w-6xl mx-auto">
        <BattleTurnModal
          isOpen={!!turnSnapshot && !battleEnded}
          turn={turnSnapshot}
          onClose={() => setTurnSnapshot(null)}
          turnTimeLeft={turnTimeLeft}
          contextLabel="On-chain PvE Turn"
        />
        <PlayerProgressionPanel playerAddress={playerAddress} />



        <div className="bg-slate-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 flex flex-col min-h-[65vh]">

          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-8 baseapp-energy-region">

            <div className="flex-1 w-full lg:w-auto baseapp-stat-panel">

              <h3 className="text-lg font-bold text-white mb-3"> You</h3>

              <div className="space-y-2 baseapp-stat-grid">

                <div className="flex justify-between baseapp-stat-chip">

                  <span className="text-slate-400 text-sm sm:text-base">Energy:</span>

                  <span className="text-white font-bold text-sm sm:text-base">{yourEnergy}/100</span>

                </div>

                <div className="flex justify-between baseapp-stat-chip">

                  <span className="text-slate-400 text-sm sm:text-base">Level:</span>

                  <span className="text-white font-bold text-sm sm:text-base">{playerStats?.level || 1}</span>

                </div>

                <div className="w-full bg-slate-600 rounded-full h-2 sm:h-3 baseapp-bar">

                  <div className="bg-blue-500 h-2 sm:h-3 rounded-full transition-all duration-500" style={{ width: `${(yourEnergy / 100) * 100}%` }}></div>

                </div>

              </div>

            </div>

            

            <div className="flex flex-col items-center justify-center mx-4 lg:mx-8 space-y-4 order-first lg:order-none baseapp-vs-panel">

              <div className="text-3xl sm:text-4xl font-bold text-white text-shadow">VS</div>

              

              {!battleEnded && !turnSubmitted && (

                <div className="bg-slate-600 rounded-lg p-3 text-center min-w-[120px]">

                  <div className="text-lg sm:text-xl font-bold text-white mb-1">🔥 Turn {currentTurn}</div>

                  <div className={`text-base sm:text-lg font-bold ${turnTimeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-yellow-400'}`}>

                    [TIMER] {turnTimeLeft}s

                  </div>

                </div>

              )}

              

              {turnSubmitted && !battleEnded && (

                <div className="bg-slate-600 rounded-lg p-3 text-center min-w-[120px]">

                  <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>

                  <p className="text-gray-300 text-sm">Processing...</p>

                </div>

              )}

              

            </div>

            

            <div className="flex-1 w-full lg:w-auto baseapp-stat-panel">

              <h3 className="text-lg font-bold text-white mb-3"> Bot</h3>

              <div className="space-y-2 baseapp-stat-grid">

                <div className="flex justify-between baseapp-stat-chip">

                  <span className="text-slate-400 text-sm sm:text-base">Energy:</span>

                  <span className="text-white font-bold text-sm sm:text-base">{opponentEnergy}/100</span>

                </div>

                <div className="flex justify-between baseapp-stat-chip">

                  <span className="text-slate-400 text-sm sm:text-base">Level:</span>

                  <span className="text-white font-bold text-sm sm:text-base">{botLevel}</span>

                </div>

                <div className="w-full bg-slate-600 rounded-full h-2 sm:h-3 baseapp-bar">

                  <div className="bg-red-500 h-2 sm:h-3 rounded-full transition-all duration-500" style={{ width: `${(opponentEnergy / 100) * 100}%` }}></div>

                </div>

              </div>

            </div>

          </div>

        </div>



        {!battleEnded && !turnSubmitted && (

          <div className="bg-slate-700 rounded-xl p-4 sm:p-6 mb-6 flex flex-col min-h-[65vh]">

            <div className="flex justify-between items-center mb-4 sm:mb-6">

              <h3 className="text-lg sm:text-xl font-bold text-white">Make Your Choice</h3>

              {turnTimeLeft <= 10 && (

                <div className="text-base sm:text-lg font-bold text-red-400 animate-pulse">

                  [TIMER] {turnTimeLeft}s

                </div>

              )}

            </div>

            

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">

              <div>

                <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-200">Choose Origami</h4>

                <div className="grid grid-cols-3 gap-2 sm:gap-3">

                  {(['rock', 'paper', 'scissors'] as Choice[]).map((ch) => (

                    <button

                      key={ch}

                      onClick={() => setSelectedChoice(ch)}

                      className={`p-2 sm:p-4 rounded-lg text-center transition-all duration-200 ${

                        selectedChoice === ch

                          ? 'bg-blue-600/50 border-2 border-blue-400 scale-105'

                          : 'bg-slate-600 hover:bg-slate-500 border border-slate-500'

                      }`}

                    >

                      <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 flex items-center justify-center min-h-[48px] sm:min-h-[64px]">

                        <img src={getChoiceImage(ch)} alt={ch} className="max-w-full max-h-full object-contain" />

                      </div>

                      <p className="text-white font-semibold text-xs sm:text-sm capitalize">{ch}</p>

                    </button>

                  ))}

                </div>

              </div>



              <div>

                <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-200">Choose Element (Optional)</h4>

                <div className="grid grid-cols-2 gap-2 sm:gap-3">

                  {(['water', 'fire', 'ice', 'wind'] as Element[]).map((elemOption) => (

                    <button

                      key={elemOption}

                      onClick={() => setElement(element === elemOption ? null : elemOption)}

                      className={`p-3 sm:p-4 rounded-lg text-center transition-all duration-200 ${

                        element === elemOption

                          ? 'border-2 border-yellow-400 scale-105'

                          : 'bg-slate-600 hover:bg-slate-500 border border-slate-500'

                      } ${

                        elemOption === 'water'

                          ? 'bg-blue-900/50'

                          : elemOption === 'fire'

                            ? 'bg-red-900/50'

                            : elemOption === 'ice'

                              ? 'bg-cyan-900/50'

                              : 'bg-green-900/50'

                      }`}

                    >

                      <div className="text-3xl sm:text-4xl mb-2">{getElementEmoji(elemOption)}</div>

                      <p className="text-white font-semibold text-xs sm:text-sm capitalize">{elemOption}</p>

                    </button>

                  ))}

                </div>

                <button

                  onClick={() => setElement(null)}

                  className="mt-3 w-full p-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-xs sm:text-sm"

                >

                  No Element

                </button>

              </div>

            </div>



            <button

              onClick={() => handleSubmitTurn()}

              disabled={!selectedChoice || isSubmitting}

              className={`mt-4 sm:mt-6 w-full py-3 sm:py-4 rounded-xl font-bold text-base sm:text-xl transition-all duration-200 ${

                selectedChoice && !isSubmitting

                  ? 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white'

                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'

              }`}

            >

              {isSubmitting ? '⏳ Submitting...' : '🔥 Submit Turn'}

            </button>

          </div>

        )}



        {turnSubmitted && !battleEnded && (

          <div className="bg-slate-700 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 text-center">

            <div className="animate-pulse">

              <p className="text-lg sm:text-xl font-bold mb-2">⏳ Processing Turn...</p>

              <p className="text-gray-300 text-sm sm:text-base">Waiting for blockchain confirmation...</p>

            </div>

          </div>

        )}



        {battleLog.length > 0 && (

          <div className="bg-slate-700 rounded-xl overflow-hidden">

            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-600/70">

              <h3 className="text-lg sm:text-xl font-bold text-white"> Battle Log</h3>

            </div>

            <div className="max-h-96 overflow-y-auto divide-y divide-slate-600/70">

              {[...battleLog].reverse().map((result, index) => (

                <div key={index} className="p-3 sm:p-4">

                  <div className="flex justify-between items-center mb-2 sm:mb-3">

                    <div className="text-white font-bold text-sm sm:text-base">Turn {result.turnNumber}</div>

                    <div className="flex gap-2">

                      {result.yourRarity && result.yourRarity !== 'NONE' && (

                        <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: RARITY_INFO[result.yourRarity].color, color: 'white' }}>

                          ✅ {result.yourRarity}

                        </span>

                      )}

                      {result.opponentRarity && result.opponentRarity !== 'NONE' && (

                        <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: RARITY_INFO[result.opponentRarity].color, color: 'white' }}>

                          ✅ {result.opponentRarity}

                        </span>

                      )}
                      {renderRarityBadge(result.opponentBreakdown?.rarityName)}

                    </div>

                  </div>

                  

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-2 sm:mb-3">

                    <div className="bg-slate-500 rounded p-2 sm:p-3">

                      <div className="text-green-400 font-semibold mb-1 text-sm sm:text-base">YOU</div>

                      <div className="flex items-center space-x-2 mb-1">

                        <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">

                          <img src={getChoiceImage(result.yourChoice)} alt={result.yourChoice} className="max-w-full max-h-full object-contain" />

                        </div>

                        <span className="text-white text-xs sm:text-sm">{getElementEmoji(result.yourElement)}</span>

                        <span className="text-white text-xs sm:text-sm">{result.yourDamage} dmg</span>

                      </div>

                      {result.yourBuffs.length > 0 && (

                        <div className="flex flex-wrap gap-1 mt-1">

                          {result.yourBuffs.map((buffId, idx) => {

                            const buffEmojis: Record<number, string> = { 0: '🛡️', 1: '🗡️', 2: '⚡', 3: '❄️' }

                            const buffNames: Record<number, string> = { 0: 'SHIELD', 1: 'PIERCE', 2: 'HASTE', 3: 'FROST' }

                            const buffName = buffNames[buffId] as BuffName

                            const isActivated = result.yourActivatedBuffs.includes(buffName)

                            const isFrostUsed = buffName === 'FROST' && result.yourFrostUsed

                            

                            let colorClass = 'bg-red-600 text-white'

                            if (isActivated) {

                              colorClass = 'bg-green-600 text-white font-bold'

                            } else if (isFrostUsed) {

                              colorClass = 'bg-slate-600 text-slate-300'

                            }

                            

                            return (
                              <span key={idx} className={`px-2 py-1 rounded text-xs ${colorClass}`}>
                                {buffEmojis[buffId]} {buffName}
                              </span>
                            )

                          })}

                        </div>

                      )}
                      {renderRarityBadge(result.yourBreakdown?.rarityName)}

                    </div>

                    

                    <div className="bg-slate-500 rounded p-2 sm:p-3">

                      <div className="text-red-400 font-semibold mb-1 text-sm sm:text-base">OPPONENT</div>

                      <div className="flex items-center space-x-2 mb-1">

                        <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">

                          <img src={getChoiceImage(result.opponentChoice)} alt={result.opponentChoice} className="max-w-full max-h-full object-contain" />

                        </div>

                        <span className="text-white text-xs sm:text-sm">{getElementEmoji(result.opponentElement)}</span>

                        <span className="text-white text-xs sm:text-sm">{result.opponentDamage} dmg</span>

                      </div>

                      {result.opponentBuffs.length > 0 && (

                        <div className="flex flex-wrap gap-1 mt-1">

                          {result.opponentBuffs.map((buffId, idx) => {

                            const buffEmojis: Record<number, string> = { 0: '🛡️', 1: '🗡️', 2: '⚡', 3: '❄️' }

                            const buffNames: Record<number, string> = { 0: 'SHIELD', 1: 'PIERCE', 2: 'HASTE', 3: 'FROST' }

                            const buffName = buffNames[buffId] as BuffName

                            const isActivated = result.opponentActivatedBuffs.includes(buffName)

                            const isFrostUsed = buffName === 'FROST' && result.opponentFrostUsed



                            let colorClass = 'bg-red-600 text-white'

                            if (isActivated) {

                              colorClass = 'bg-green-600 text-white font-bold'

                            } else if (isFrostUsed) {

                              colorClass = 'bg-slate-600 text-slate-300'

                            }

                            

                            return (
                              <span key={idx} className={`px-2 py-1 rounded text-xs ${colorClass}`}>
                                {buffEmojis[buffId]} {buffName}
                              </span>
                            )

                          })}

                        </div>

                      )}
                      {renderRarityBadge(result.opponentBreakdown?.rarityName)}

                    </div>

                  </div>

                  

                  <div className="text-gray-300 text-xs sm:text-sm mt-2 mb-2 sm:mb-3">

                    Energy After: You {result.yourEnergy} vs Bot {result.opponentEnergy}

                  </div>



                  {result.yourBreakdown && (

                    <div className="bg-slate-900 rounded p-3 mb-2">

                      <div className="text-green-400 font-semibold mb-1"> Your Damage Breakdown:</div>

                      <div className="text-slate-300 text-xs sm:text-sm space-y-1">

                        <div>Levels: Attacker L{result.yourBreakdown.attackerLevel ?? '?'} vs Defender L{result.yourBreakdown.defenderLevel ?? '?'}</div>

                        <div>

                          Base: {result.yourBreakdown.baseWithoutLevel ?? 10} + Level Bonus {result.yourBreakdown.levelBonus} = {result.yourBreakdown.baseDamage}

                        </div>

                        <div>

                          Hand Outcome: {result.yourBreakdown.handOutcome}{' '}

                          {result.yourBreakdown.handMultiplier && result.yourBreakdown.handMultiplier !== 1 ? `(x${result.yourBreakdown.handMultiplier})` : '(x1)'}

                          {result.yourBreakdown.damageAfterHand !== undefined && `  ${Math.round(result.yourBreakdown.damageAfterHand)}`}

                        </div>

                        <div>

                          Element: {result.yourBreakdown.elementName}{' '}

                          {formatBonusLabel(result.yourBreakdown.elementBonus)}

                          {result.yourBreakdown.damageAfterElement !== undefined && `  ${Math.round(result.yourBreakdown.damageAfterElement)}`}

                        </div>

                        <div>

                          Rarity:{' '}

                          {result.yourBreakdown.rarityBonus > 0

                            ? `${result.yourBreakdown.rarityName} (+${result.yourBreakdown.rarityBonus})${

                                result.yourBreakdown.damageAfterCrit !== undefined

                                  ? `  ${Math.round(result.yourBreakdown.damageAfterCrit)}`

                                  : ''

                              }`

                            : 'None'}

                        </div>

                        {result.yourBreakdown.hasteApplied && (

                          <div>

                            HASTE: +{result.yourBreakdown.hasteBonus}  {Math.round(result.yourBreakdown.rawDamage)}

                          </div>

                        )}

                        <div>

                          Defense Multiplier: x{result.yourBreakdown.defense?.toFixed(2)}

                          {result.yourBreakdown.pierceApplied && result.yourBreakdown.pierceReduction !== undefined

                            ? ` (Pierce +${Math.round(result.yourBreakdown.pierceReduction)})`

                            : ''}

                        </div>

                        <div>

                          After Defense: {Math.round(result.yourBreakdown.afterDefense)}

                        </div>

                        {result.yourBreakdown.frostApplied && result.yourBreakdown.frostReduction !== undefined && (

                          <div className="text-red-400">Frost blocked {Math.round(result.yourBreakdown.frostReduction)} damage ❄️</div>

                        )}

                        {result.yourBreakdown.shieldApplied && result.yourBreakdown.shieldReduction !== undefined && (

                          <div className="text-blue-400">Shield blocked {Math.round(result.yourBreakdown.shieldReduction)} damage </div>

                        )}

                        <div className="text-green-300 font-bold">Final Damage: {result.yourDamage}</div>

                      </div>

                    </div>

                  )}



                  {result.opponentBreakdown && (

                    <div className="bg-slate-900 rounded p-3">

                      <div className="text-red-400 font-semibold mb-1"> Bot Damage Breakdown:</div>

                      <div className="text-slate-300 text-xs sm:text-sm space-y-1">

                        <div>Levels: Attacker L{result.opponentBreakdown.attackerLevel ?? '?'} vs Defender L{result.opponentBreakdown.defenderLevel ?? '?'}</div>

                        <div>

                          Base: {result.opponentBreakdown.baseWithoutLevel ?? 10} + Level Bonus {result.opponentBreakdown.levelBonus} = {result.opponentBreakdown.baseDamage}

                        </div>

                        <div>

                          Hand Outcome: {result.opponentBreakdown.handOutcome}{' '}

                          {result.opponentBreakdown.handMultiplier && result.opponentBreakdown.handMultiplier !== 1 ? `(x${result.opponentBreakdown.handMultiplier})` : '(x1)'}

                          {result.opponentBreakdown.damageAfterHand !== undefined && `  ${Math.round(result.opponentBreakdown.damageAfterHand)}`}

                        </div>

                        <div>

                          Element: {result.opponentBreakdown.elementName}{' '}

                          {formatBonusLabel(result.opponentBreakdown.elementBonus)}

                          {result.opponentBreakdown.damageAfterElement !== undefined && `  ${Math.round(result.opponentBreakdown.damageAfterElement)}`}

                        </div>

                        <div>

                          Rarity:{' '}

                          {result.opponentBreakdown.rarityBonus > 0

                            ? `${result.opponentBreakdown.rarityName} (+${result.opponentBreakdown.rarityBonus})${

                                result.opponentBreakdown.damageAfterCrit !== undefined

                                  ? `  ${Math.round(result.opponentBreakdown.damageAfterCrit)}`

                                  : ''

                              }`

                            : 'None'}

                        </div>

                        {result.opponentBreakdown.hasteApplied && (

                          <div>

                            HASTE: +{result.opponentBreakdown.hasteBonus}  {Math.round(result.opponentBreakdown.rawDamage)}

                          </div>

                        )}

                        <div>

                          Defense Multiplier: x{result.opponentBreakdown.defense?.toFixed(2)}

                          {result.opponentBreakdown.pierceApplied && result.opponentBreakdown.pierceReduction !== undefined

                            ? ` (Pierce +${Math.round(result.opponentBreakdown.pierceReduction)})`

                            : ''}

                        </div>

                        <div>

                          After Defense: {Math.round(result.opponentBreakdown.afterDefense)}

                        </div>

                        {result.opponentBreakdown.shieldApplied && result.opponentBreakdown.shieldReduction !== undefined && (

                          <div className="text-blue-400">Your SHIELD blocked {Math.round(result.opponentBreakdown.shieldReduction)} damage </div>

                        )}

                        {result.opponentBreakdown.frostApplied && result.opponentBreakdown.frostReduction !== undefined && (

                          <div className="text-blue-400">Your FROST blocked {Math.round(result.opponentBreakdown.frostReduction)} damage ❄️</div>

                        )}

                        <div className="text-red-300 font-bold">Final Damage: {result.opponentDamage}</div>

                      </div>

                    </div>

                  )}

                </div>

              ))}

            </div>

          </div>

        )}

        

        {battleLog.length === 0 && !battleEnded && (

          <div className="bg-slate-700 rounded-xl overflow-hidden">

            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-600/70">

              <h3 className="text-lg sm:text-xl font-bold text-white"> Battle Log</h3>

            </div>

            <p className="text-center text-gray-400 text-sm sm:text-base py-6">No turns played yet...</p>

          </div>

        )}



        {battleEnded && (

          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4">

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-4 sm:p-6 md:p-8 max-w-md w-full mx-4 border-2 border-blue-500">

              <div className="text-center">

                {winner === null ? (

                  <>

                    <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 text-yellow-400"> Draw!</h2>

                    <p className="text-lg sm:text-xl mb-4 sm:mb-6">Both players ran out of HP</p>

                  </>

                ) : winner === playerAddress.toLowerCase() ? (

                  <>

                    <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 text-green-400"> Victory!</h2>

                    <p className="text-lg sm:text-xl mb-4 sm:mb-6">You defeated the bot!</p>

                  </>

                ) : (

                  <>

                    <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 text-red-400"> Defeat</h2>

                    <p className="text-lg sm:text-xl mb-4 sm:mb-6">The bot won this time...</p>

                  </>

                )}

                    <p className="text-gray-300 mb-2 text-sm sm:text-base">Turns: {battleLog.length}</p>

                {resolutionHash && (

                  <a

                    href={`https://sepolia.basescan.org/tx/${resolutionHash}`}

                    target="_blank"

                    rel="noopener noreferrer"

                    className="text-blue-400 text-sm underline"

                  >

                    View resolution on BaseScan

                  </a>

                )}

              </div>

            </div>

          </div>

        )}



        <button

          onClick={onBackToMenu}

          disabled={!battleEnded}

          className={`w-full py-2 sm:py-3 rounded-xl font-bold transition-all duration-200 text-sm sm:text-base ${

            battleEnded ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-800 text-gray-500 cursor-not-allowed'

          }`}

        >

           Back to Menu

        </button>

      </div>

    </div>

  )

}





